import React from 'react';
import { Link, useParams } from 'react-router-dom';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { Badge, RegionState, SkeletonLoader } from '@freelancelabs/components';

import { ListSkeleton } from '../../components/Skeletons';
import { OrganizationKind } from '../../graphql/__generated__/schema';
import { AccessibleAccountsDocument } from '../../graphql/__generated__/types';
import DashboardCataloguePanel from '../catalogues/DashboardCataloguePanel';
import AccountModulesPanel from './AccountModulesPanel';
import MfaRequirementPanel from './MfaRequirementPanel';
import PortalAccessPanel from './PortalAccessPanel';
import { accountLookupRefusalOf } from './accountsRefusal';

/**
 * The sheet of one account (PCG-141), and the home the three configuration panels were
 * waiting for.
 *
 * **What moved, and why it is a move rather than a second door.** Opening an account to
 * the portal, opening its modules and declaring its dashboards used to be reached by
 * filtering the **users list** on one account: PCG-79 put them there because filtering
 * on an account is what made it the subject of a screen, and because a « Comptes »
 * module did not exist. It does now, so the panels come here and **do not stay there**:
 * two paths to one gesture are two places to correct, and the day one of them gains a
 * refusal the other keeps offering the button.
 *
 * The order is the order of the decisions: an account is opened to the portal first,
 * then the modules it may see are opened, then what it may be shown inside one of them
 * is declared. All three take the same permission, `manage_accounts`, which is why they
 * sit together, and the third is the content of the second.
 *
 * **Attention, the account is named through `accessibleAccounts` and not through the
 * list this sheet is opened from.** `client-hub-api` has **no single-account read**:
 * `accounts` pages and searches but cannot be asked for one identifier, and its `Account`
 * deliberately carries no `kind`, which the three panels need in order to drop out on
 * Freelance.com. So the perimeter query is what names the account, exactly as the users
 * list already used it for the same purpose. Two consequences to know:
 *
 * - the whole perimeter travels to name one account, which is 7 583 rows on the parc.
 *   That is the cost of the missing read, and it is `client-hub-api`'s to remove, not
 *   this screen's to work around;
 * - `PortalAccessDialog` already refetches that very document, so opening or closing the
 *   account updates this header on its own. That loop is why the header is not built
 *   from router state, which would also have died on a reload.
 */
const AccountDetailPage = () => {
    const { accountId } = useParams<{ accountId: string }>();

    const { data, loading, error } = useQuery(AccessibleAccountsDocument);

    const account = (data?.accessibleAccounts ?? []).find((candidate) => candidate?.id === accountId);

    const backToList = (
        <Link to="/comptes" className="link text-sm">
            {c('Accounts').t`Back to the accounts`}
        </Link>
    );

    if (loading && !data) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                {/*
                    LA FICHE SE DESSINE. Un compte dont on ne sait pas encore le nom n'est
                    pas un compte sans nom : le titre reste un bloc tant qu'on ne sait pas,
                    plutot qu'un titre vide qui saute ensuite. Les quatre panneaux sont
                    dessines parce que la fiche en porte quatre : trois feraient sauter la
                    page d'un panneau a l'arrivee de la reponse.
                */}
                <div className="flex flex-col gap-5" aria-busy="true">
                    <SkeletonLoader className="w-1/3" height={26} />
                    <SkeletonLoader className="w-1/4" height={14} />
                    {[0, 1, 2, 3].map((rang) => (
                        <div className="rounded-xl border border-border-norm p-5" key={rang}>
                            <SkeletonLoader className="mb-4 w-1/4" height={18} />
                            <ListSkeleton rows={3} />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    if (error) {
        const refusal = accountLookupRefusalOf(error);
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <RegionState
                    tone="error"
                    title={refusal.title}
                    description={
                        <>
                            {refusal.guidance}
                            {refusal.detail && (
                                <span className="mt-2 block break-words rounded-md border border-border-norm bg-background-weak p-2 font-mono text-xs">
                                    {refusal.detail}
                                </span>
                            )}
                        </>
                    }
                />
            </section>
        );
    }

    /**
     * Not found, and the two reasons are **not told apart on purpose**: the account may
     * not exist, or it may sit outside the portfolio. The screen cannot know which,
     * because the perimeter is what it looks the account up in, and it must not guess,
     * since « ce compte n'existe pas » about an account somebody else administers is a
     * false statement about the base. So the sentence says exactly what happened.
     */
    if (!account?.id) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <RegionState
                    title={c('Accounts').t`This account is not one you can act on.`}
                    description={c('Accounts')
                        .t`Either it does not exist, or it is outside your portfolio, and the address alone cannot tell which, since accounts are looked up among the ones you may act on. If you reached this page from a link somebody sent you, ask them which account it was.`}
                />
            </section>
        );
    }

    const name = account.name ?? account.id;
    // ttag refuses an expression inside a localized template, so the name is bound to a
    // plain identifier before the string that holds it.
    const companyName = account.rootOrganizationName;

    /**
     * Freelance.com is reachable here only by typing its identifier: the list excludes it
     * on purpose, every gesture below refusing it with
     * `INTERNAL_ORGANIZATION_NOT_ALLOWED`. Left to render, the three panels would each
     * drop out on their own and the sheet would be a heading over nothing, which reads as
     * a broken page rather than as a row that has no business here.
     */
    if (account.kind === OrganizationKind.Internal) {
        return (
            <section className="flex flex-col gap-5">
                {backToList}
                <h1 className="text-xl font-semibold text-text-900">{name}</h1>
                <RegionState
                    title={c('Accounts').t`Freelance.com is not a client account.`}
                    description={c('Accounts')
                        .t`It carries no portal access, no portal module and no dashboard catalogue: the back-office is a separate application and none of them gates it. That is why it is absent from the accounts list, and it stays in your perimeter so that you can administer internal colleagues from the users screen.`}
                />
            </section>
        );
    }

    return (
        <section className="flex flex-col gap-5">
            {backToList}

            <header className="flex flex-wrap items-start gap-4">
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                        <h1 className="text-xl font-semibold text-text-900">{name}</h1>
                        {/* Null is not « fermé »: a missing field would announce a state
                            nobody set, on the very fact the first panel exists to change. */}
                        {account.portalEnabled !== null && account.portalEnabled !== undefined && (
                            <Badge category={account.portalEnabled ? 'success' : 'default'} size="small">
                                {account.portalEnabled
                                    ? c('Portal access').t`Open to the portal`
                                    : c('Portal access').t`Closed to the portal`}
                            </Badge>
                        )}
                    </div>
                    {/* The company, read and never linked: it may sit outside the
                        portfolio, so its sheet is not always reachable. Said only when the
                        account is not its own root: on the parc that is one account in
                        twelve, and repeating the account's own name under it would be a
                        line that adds nothing on all the others. */}
                    {account.rootOrganizationId && account.rootOrganizationId !== account.id && (
                        <p className="mt-1 text-xs text-text-600">
                            {companyName
                                ? c('Accounts').t`Head office: ${companyName}`
                                : c('Accounts').t`Belongs to a company this list does not name.`}
                        </p>
                    )}
                </div>
            </header>

            <PortalAccessPanel
                accountId={account.id}
                accountName={name}
                portalEnabled={account.portalEnabled}
                kind={account.kind}
            />
            {/* Après l'accès et avant les modules, et l'ordre n'est pas cosmétique : les
                quatre panneaux se lisent comme une suite de décisions sur un client, du
                plus général au plus précis. Le compte peut-il recevoir des gens, comment
                doivent-ils s'authentifier, que voient-ils, et quoi à l'intérieur d'un
                module. L'authentification vient donc juste après la porte. */}
            <MfaRequirementPanel
                accountId={account.id}
                accountName={name}
                mfaRequired={account.mfaRequired}
                kind={account.kind}
            />
            <AccountModulesPanel accountId={account.id} accountName={name} kind={account.kind} />
            <DashboardCataloguePanel
                accountId={account.id}
                accountName={name}
                kind={account.kind}
                salesforceId={account.salesforceId}
            />
        </section>
    );
};

export default AccountDetailPage;
